// Two controls frame 14 draws that `frontend/src/ui/` does not ship.
//
// `Primitive/Chip` (8:27) and `Data/Pagination` (26:63) were both explicitly
// left unported by the Wave 1 primitives agent, which asked that a screen
// needing them say so rather than build a seventh private copy. Only this
// screen needs either today, so they live here, smallest-thing-that-works,
// and are flagged in the progress notes as the first two candidates to hoist
// into `ui/` the moment a second screen wants them.

import "./content.css";
import { Button, Icon } from "../../ui";

export type FilterChipProps = {
  label: string;
  pressed: boolean;
  onToggle: () => void;
};

/** Port of `Primitive/Chip`: `aria-pressed` is the state, not a class. */
export function FilterChip({ label, pressed, onToggle }: FilterChipProps) {
  return (
    <button
      type="button"
      className="maisight-content-chip type-label"
      aria-pressed={pressed}
      onClick={onToggle}
    >
      {label}
    </button>
  );
}

export type PagerProps = {
  /** Accessible name — there is more than one list on some screens. */
  label: string;
  offset: number;
  limit: number;
  total: number;
  onOffsetChange: (offset: number) => void;
};

/**
 * Server-side pager. The Table primitive states "Showing 20 of 60 documents"
 * on its own; this supplies the control that reaches the other 40, which is
 * the half of US-120's volume criterion a row cap alone does not satisfy.
 */
export function Pager({
  label,
  offset,
  limit,
  total,
  onOffsetChange,
}: PagerProps) {
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit) + 1;
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  if (pageCount === 1) return null;

  return (
    <nav className="maisight-content-pager" aria-label={label}>
      <Button
        size="sm"
        variant="ghost"
        disabled={!hasPrev}
        onClick={() => onOffsetChange(Math.max(0, offset - limit))}
        leading={<Icon name="chevron-left" size={14} />}
      >
        Previous
      </Button>
      <span className="maisight-content-pager-count type-numSm">
        {`Page ${page} of ${pageCount}`}
      </span>
      <Button
        size="sm"
        variant="ghost"
        disabled={!hasNext}
        onClick={() => onOffsetChange(offset + limit)}
      >
        Next
      </Button>
    </nav>
  );
}
